import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import type { useAltaDeProducto } from "../hooks/useAltaDeProducto";
import type { ProductImage } from "./VariantForm";
import { MAX_IMAGENES_POR_PRODUCTO } from "../lib/limiteImagenesProducto";
import { datosDeLaFicha, guardarDatosDelPdf, leerPdfDelProveedor, nombreTrasLeerPdf, type DatosDelPdf } from "../lib/fichaDesdePdf";

/**
 * El PDF del proveedor en "Un solo producto": se suelta en la MISMA zona de las fotos
 * (ADR-2026-10-18-el-pdf-entra-por-la-subida-de-siempre, supersede la opción aparte de
 * ADR-2026-10-18-el-producto-se-carga-desde-el-pdf-del-proveedor). Haiku propone nombre, fotos y
 * datos; nombre y fotos se vuelcan en el alta de siempre (`useAltaDeProducto`) y lo propio —las
 * demás fotos del PDF y los datos— se ve debajo y se guarda en la ficha después de crear.
 */

type Alta = ReturnType<typeof useAltaDeProducto>;
type Fase = "nada" | "leyendo" | "listo" | "error";

export interface FotoDelPdfEnPantalla {
  numero: number;
  pagina: number;
  url: string;
  archivo: File;
}

const DATOS_VACIOS: DatosDelPdf = { campos: [], medidas: null, paquete: null, dudas: [], variosProductos: false };

const comoImagenDelAlta = (f: FotoDelPdfEnPantalla, i: number): ProductImage => ({
  id: -(f.numero + 1000),
  url: f.url,
  principal: i === 0,
  orden: i + 1,
  producto_variante_id: 0,
  tempFile: f.archivo,
  fileName: f.archivo.name,
});

/** El estado del PDF soltado. Lo monta quien monta el alta (la pantalla o el pop-up). */
export function useCargaDesdePdf(alta: Alta) {
  const [fase, setFase] = useState<Fase>("nada");
  const [error, setError] = useState<string | null>(null);
  const [archivo, setArchivo] = useState<string | null>(null);
  const [fotos, setFotos] = useState<FotoDelPdfEnPantalla[]>([]);
  const [datos, setDatos] = useState<DatosDelPdf>(DATOS_VACIOS);
  const urls = useRef<string[]>([]);
  // Cada lectura tiene su turno: si se suelta otro PDF (o se descarta) mientras uno se lee, la
  // respuesta vieja se ignora. Y el nombre que puso un PDF se reemplaza con el del siguiente; sólo
  // se respeta el que escribió la persona (ADR-2026-10-19-revision-greptile-pdf-y-catalogo).
  const turno = useRef(0);
  const nombreDelPdf = useRef<string | null>(null);

  const soltarUrls = useCallback(() => {
    urls.current.forEach((u) => URL.revokeObjectURL(u));
    urls.current = [];
  }, []);
  useEffect(() => soltarUrls, [soltarUrls]);

  const reiniciar = useCallback(() => {
    turno.current += 1;
    nombreDelPdf.current = null;
    soltarUrls();
    setFase("nada");
    setFotos([]);
    setDatos(DATOS_VACIOS);
    setArchivo(null);
    setError(null);
  }, [soltarUrls]);

  const leer = useCallback(
    async (pdf: File) => {
      const mio = ++turno.current;
      soltarUrls();
      setDatos(DATOS_VACIOS);
      setFotos([]);
      setError(null);
      setArchivo(pdf.name);
      setFase("leyendo");
      const { ficha, fotos: leidas, error: e } = await leerPdfDelProveedor(pdf);
      if (mio !== turno.current) return;
      const enPantalla = leidas.map((f) => {
        const url = URL.createObjectURL(f.blob);
        urls.current.push(url);
        return { numero: f.numero, pagina: f.pagina, url, archivo: new File([f.blob], `pdf-p${f.pagina}-${f.numero}.jpg`, { type: "image/jpeg" }) };
      });
      setFotos(enPantalla);
      if (!ficha) {
        setError(e);
        setFase("error");
        return;
      }
      const porNumero = new Map(enPantalla.map((f) => [f.numero, f]));
      const elegidas = ficha.fotos
        .map((n) => porNumero.get(n))
        .filter((f): f is FotoDelPdfEnPantalla => !!f)
        .slice(0, MAX_IMAGENES_POR_PRODUCTO);
      // El PDF reemplaza lo que hubiera: es otro producto. El nombre se respeta si lo escribió la
      // persona; si lo puso el PDF anterior, va el de este.
      const anterior = nombreDelPdf.current;
      nombreDelPdf.current = ficha.nombre;
      alta.setProducto((p) => ({ nombre: nombreTrasLeerPdf(p.nombre, anterior, ficha.nombre), images: elegidas.map(comoImagenDelAlta) }));
      setDatos(datosDeLaFicha(ficha));
      setFase("listo");
    },
    [alta, soltarUrls],
  );

  /** Suma o saca una foto del PDF de las del producto (hasta el tope de siempre). */
  const alternarFoto = useCallback(
    (f: FotoDelPdfEnPantalla) => {
      alta.setProducto((p) => {
        const esta = p.images.some((i) => i.url === f.url);
        if (!esta && p.images.length >= MAX_IMAGENES_POR_PRODUCTO) return p;
        const siguientes = esta ? p.images.filter((i) => i.url !== f.url) : [...p.images, comoImagenDelAlta(f, p.images.length)];
        return { ...p, images: siguientes.map((i, k) => ({ ...i, principal: k === 0, orden: k + 1 })) };
      });
    },
    [alta],
  );

  /**
   * Crea el producto (el guardado de siempre) y, si vino de un PDF, guarda su ficha. Devuelve true
   * si el producto quedó creado.
   */
  const guardar = useCallback(async (): Promise<boolean> => {
    const nombre = alta.producto.nombre.trim();
    const varianteId = await alta.guardar();
    if (!varianteId) return false;
    if (fase === "listo") {
      const texto = await guardarDatosDelPdf(varianteId, nombre, datos);
      if (/No guardé/.test(texto)) console.warn("[PDF del proveedor]", texto);
    }
    reiniciar();
    return true;
  }, [alta, datos, fase, reiniciar]);

  return { fase, error, archivo, fotos, datos, setDatos, leer, alternarFoto, guardar, descartar: reiniciar };
}

export type CargaDesdePdf = ReturnType<typeof useCargaDesdePdf>;

const TARJETA =
  "rounded-2xl bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_0_0_1px_rgba(16,24,40,0.06)] sm:px-5 sm:py-4";

/** Arriba del cuerpo: leyendo, no se pudo, o qué conviene revisar. Nada si no se soltó un PDF. */
export const AvisoDelPdf: React.FC<{ carga: CargaDesdePdf }> = ({ carga }) => {
  if (carga.fase === "leyendo") {
    return (
      <div className={`${TARJETA} flex items-center gap-3`} role="status">
        <Loader2 className="h-5 w-5 shrink-0 animate-spin text-brand-primary" />
        <div className="min-w-0">
          <p className="truncate text-[14.5px] font-semibold text-[#16181D]">Leyendo «{carga.archivo}»…</p>
          <p className="text-[13px] text-black/50">Sacamos las fotos y los datos del producto. Tarda unos 15 segundos.</p>
        </div>
      </div>
    );
  }
  if (carga.fase === "error") {
    return (
      <div className="flex items-start justify-between gap-3 rounded-2xl bg-[#FFF4E5] px-4 py-3 text-[13.5px] text-[#8A5300]">
        <p>
          «{carga.archivo}»: {carga.error}
        </p>
        <button type="button" onClick={carga.descartar} aria-label="Cerrar" className="shrink-0 opacity-60 hover:opacity-100">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
  const { datos } = carga;
  if (carga.fase !== "listo" || (!datos.variosProductos && datos.dudas.length === 0)) return null;
  return (
    <div className="rounded-2xl bg-[#FFF4E5] px-4 py-3 text-[13.5px] leading-snug text-[#8A5300]">
      <p className="mb-1 inline-flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="h-4 w-4" /> Revisá antes de guardar
      </p>
      <ul className="list-disc space-y-0.5 pl-5">
        {datos.variosProductos && <li>El PDF parece tener varios productos: cargamos el principal.</li>}
        {datos.dudas.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>
    </div>
  );
};

/** Debajo de las fotos: las demás fotos del PDF para sumar o sacar, y los datos que se van a guardar. */
export const FotosYDatosDelPdf: React.FC<{ carga: CargaDesdePdf; alta: Alta }> = ({ carga, alta }) => {
  if (carga.fase !== "listo") return null;
  const { datos } = carga;
  const elegidas = new Set(alta.producto.images.map((i) => i.url));
  const lleno = alta.producto.images.length >= MAX_IMAGENES_POR_PRODUCTO;
  const quitarCampo = (k: number) => carga.setDatos({ ...datos, campos: datos.campos.filter((_, i) => i !== k) });
  const cambiarValor = (k: number, valor: string) =>
    carga.setDatos({ ...datos, campos: datos.campos.map((c, i) => (i === k ? { ...c, valor } : c)) });
  const medida = (n: number | null | undefined) => (n ? String(n).replace(".", ",") : "–");

  return (
    <>
      {carga.fotos.length > 0 && (
        <div className={TARJETA}>
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <p className="text-[14px] font-semibold text-[#16181D]">Más fotos del PDF</p>
            <span className="text-[12.5px] text-black/40">Tocá una para sumarla o sacarla</span>
          </div>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8">
            {carga.fotos.map((f) => {
              const esta = elegidas.has(f.url);
              return (
                <button
                  key={f.numero}
                  type="button"
                  onClick={() => carga.alternarFoto(f)}
                  disabled={alta.guardando || (!esta && lleno)}
                  title={`Página ${f.pagina}`}
                  className={`relative aspect-square overflow-hidden rounded-xl bg-[#F4F5F7] transition-shadow disabled:cursor-not-allowed ${esta ? "shadow-[0_0_0_2.5px_var(--brand-primary)]" : "hover:shadow-[0_0_0_1.5px_rgb(var(--brand-primary-rgb)_/_0.5)] disabled:opacity-40"}`}
                >
                  <img src={f.url} alt="" className="h-full w-full object-cover" loading="lazy" />
                  {esta && (
                    <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-white">
                      <Check className="h-3 w-3" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {(datos.campos.length > 0 || datos.medidas || datos.paquete) && (
        <div className={TARJETA}>
          <p className="mb-3 text-[14px] font-semibold text-[#16181D]">Datos del PDF</p>
          <ul className="flex flex-col divide-y divide-black/[0.06]">
            {datos.medidas && (
              <li className="flex items-center gap-3 py-2 text-[13.5px]">
                <span className="w-40 shrink-0 text-black/50">Medidas ({datos.medidas.unidad})</span>
                <span className="flex-1 text-[#16181D]">
                  Alto {medida(datos.medidas.alto)} · Ancho {medida(datos.medidas.ancho)} · Prof. {medida(datos.medidas.profundidad)}
                  {datos.medidas.peso_kg ? ` · ${medida(datos.medidas.peso_kg)} kg` : ""}
                </span>
                <BotonQuitar onClick={() => carga.setDatos({ ...datos, medidas: null })} />
              </li>
            )}
            {datos.paquete && (
              <li className="flex items-center gap-3 py-2 text-[13.5px]">
                <span className="w-40 shrink-0 text-black/50">Caja de envío (cm)</span>
                <span className="flex-1 text-[#16181D]">
                  {medida(datos.paquete.alto)} × {medida(datos.paquete.ancho)} × {medida(datos.paquete.profundidad)}
                  {datos.paquete.peso_kg ? ` · ${medida(datos.paquete.peso_kg)} kg` : ""}
                </span>
                <BotonQuitar onClick={() => carga.setDatos({ ...datos, paquete: null })} />
              </li>
            )}
            {datos.campos.map((c, k) => (
              <li key={`${c.clave}-${k}`} className="flex items-center gap-3 py-1.5 text-[13.5px]">
                <span className="w-40 shrink-0 truncate text-black/50">{c.clave || "Característica"}</span>
                <input
                  value={c.valor}
                  onChange={(e) => cambiarValor(k, e.target.value)}
                  disabled={alta.guardando}
                  className="min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-2 py-1 text-[#16181D] outline-none hover:border-black/[0.08] focus:border-brand-primary"
                />
                <BotonQuitar onClick={() => quitarCampo(k)} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

const BotonQuitar: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Quitar"
    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-black/35 hover:bg-black/[0.05] hover:text-black/70"
  >
    <X className="h-4 w-4" />
  </button>
);
